import { useState } from 'react'
import { paginationStorageKeyPrefixes } from 'src/constants/localStorageKeys'

const DEFAULT_PAGE_SIZE = 25

export type PaginationStorageKeyPrefix =
  (typeof paginationStorageKeyPrefixes)[number]

export interface PaginationState {
  page: number
  pageSize: number
}

interface UseListPaginationOptions {
  /**
   * localStorageキーのプレフィックス。
   * 実際のキーは `${storageKeyPrefix}Page` と `${storageKeyPrefix}PageSize` になる。
   * 新しい画面で使う場合は paginationStorageKeyPrefixes にプレフィックスを追加すること。
   */
  storageKeyPrefix: PaginationStorageKeyPrefix
  /**
   * デフォルトのページサイズ。省略時は 25。
   */
  defaultPageSize?: number
}

interface UseListPaginationReturn {
  /** 現在のページネーション状態 */
  pagination: PaginationState
  /** ページを変更する（pageSizeは任意で同時に変更可能） */
  changePage: (page: number, pageSize?: number) => void
  /** ページサイズを変更する（ページは自動的に1にリセットされる） */
  changePageSize: (pageSize: number) => void
  /** ページを1にリセットする（ページサイズはそのまま） */
  resetToFirstPage: () => void
}

/**
 * 一覧画面のページネーション状態（page / pageSize）を localStorage に永続化する汎用 hook。
 *
 * `@deprecated` な `useListFilter` のページネーション管理部分を置き換えるために設計された。
 * `useListFilter` はページネーション + フィルタ条件を単一の localStorage キーで全画面分まとめて管理していたが、
 * この hook はページネーションのみを担当し、画面ごとに独立した localStorage キーを使用する。
 *
 * フィルタ条件（検索、場所絞り込み等）は各画面の TanStack Query 移行時に画面ごとに個別管理する方針。
 *
 * @example
 * ```tsx
 * const { pagination, changePage, changePageSize } = useListPagination({
 *   storageKeyPrefix: 'userGroups',
 * })
 * // localStorage キーは 'userGroupsPage' と 'userGroupsPageSize' になる
 *
 * // TanStack Query と組み合わせて使う
 * const { data } = useQueryItems({
 *   limit: pagination.pageSize,
 *   offset: (pagination.page - 1) * pagination.pageSize,
 * })
 * ```
 */
export const useListPagination = (
  options: UseListPaginationOptions,
): UseListPaginationReturn => {
  const { storageKeyPrefix, defaultPageSize = DEFAULT_PAGE_SIZE } = options

  const pageKey = `${storageKeyPrefix}Page`
  const pageSizeKey = `${storageKeyPrefix}PageSize`

  const getPersistedPageSize = (): number => {
    try {
      const stored = localStorage.getItem(pageSizeKey)
      if (stored === null) return defaultPageSize
      const parsed = Number(stored)
      return Number.isFinite(parsed) && parsed > 0 ? parsed : defaultPageSize
    } catch {
      return defaultPageSize
    }
  }

  const getPersistedPage = (): number => {
    try {
      const stored = localStorage.getItem(pageKey)
      if (stored === null) return 1
      const parsed = Number(stored)
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
    } catch {
      return 1
    }
  }

  const [pagination, setPagination] = useState<PaginationState>(() => ({
    page: getPersistedPage(),
    pageSize: getPersistedPageSize(),
  }))

  const changePage = (page: number, pageSize?: number) => {
    localStorage.setItem(pageKey, String(page))
    if (pageSize !== undefined) {
      localStorage.setItem(pageSizeKey, String(pageSize))
    }
    setPagination(prev => ({
      ...prev,
      page,
      pageSize: pageSize ?? prev.pageSize,
    }))
  }

  const changePageSize = (pageSize: number) => {
    localStorage.setItem(pageSizeKey, String(pageSize))
    localStorage.setItem(pageKey, '1')
    setPagination(prev => ({
      ...prev,
      page: 1,
      pageSize,
    }))
  }

  const resetToFirstPage = () => {
    localStorage.setItem(pageKey, '1')
    setPagination(prev => ({
      ...prev,
      page: 1,
    }))
  }

  return {
    pagination,
    changePage,
    changePageSize,
    resetToFirstPage,
  }
}
